import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import Paul_Gale from "@/landing/assets/sales/Paul_Gale.png";
import brandDPL from "@/landing/assets/brands/DPL.png";
import brandLTP from "@/landing/assets/brands/LTP.png";
import brandCFPadel from "@/landing/assets/brands/cfpadel1.jpg";
import brandISD from "@/landing/assets/brands/ISD_Padel.png";
import appStoreIcon from "@/landing/assets/Apple.png";
import playStoreIcon from "@/landing/assets/Playstore.png";
import shotStandings from "@/landing/assets/League2.png";
import shotPhone from "@/landing/assets/Mobile_Profile.png";

export const Route = createFileRoute('/padel-tournament-software')({
    component: RouteComponent,
})

const PADEL_BRANDS = [
    {src: brandDPL, alt: "Dubai Padel League"},
    {src: brandLTP, alt: "Love The Padel"},
    {src: brandCFPadel, alt: "CF Padel"},
    {src: brandISD, alt: "ISD Padel League"},
];

// Paul G.'s words, reused verbatim from the homepage testimonial carousel.
const PAUL_QUOTE = `I was tired of scrolling through messy group chats, losing track of who was at the top of the league. ScorePal has been a breath of fresh air — inputting scores takes seconds and the live updates keep everyone in the loop. It actually adds a professional feel to our regular padel games and fuels the competitive spirit. I can't imagine going back to the spreadsheet I was using before!`;

// Real ScorePal live event. Swap this slug for the specific padel event you want to feature
// (e.g. a Dubai Padel League / Love the Padel public live link).
const LIVE_PADEL_EVENT_URL = "https://scorepal.co/live/leagues/scorepal-2026-league/";
const APP_STORE_URL = "https://apps.apple.com/us/app/score-pal/id6745509167";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share";

const FORMATS = [
    {name: "Americano", sub: "Social rotating doubles", to: "/americano-generator/"},
    {name: "Mexicano", sub: "Adaptive doubles pairings", to: "/mexicano-tournament-generator/"},
    {name: "Round Robin", sub: "Fair group rankings", to: "/round-robin-tournament-generator/"},
    {name: "King of the Court", sub: "Fast court rotations", to: "/king-of-the-court-generator/"},
    {name: "Ladder League", sub: "Ongoing club rankings", to: "/ladder-league-generator/"},
    {name: "Group and Knockout", sub: "Groups into finals", to: "/group-and-knockout-generator/"},
    {name: "Single Elimination", sub: "Simple knockout brackets", to: "/single-elimination-bracket-generator/"},
    {name: "Double Elimination", sub: "Second-chance brackets", to: "/double-elimination-bracket-generator/"},
];

const FEATURES = [
    {code: "RG", title: "Online registration & signup", desc: "Let players sign up online with custom forms, division selection, partner choice for doubles, waivers and entry-fee payments."},
    {code: "FX", title: "Padel fixture generation", desc: "Create fixtures for club nights, leagues, groups and knockout events."},
    {code: "SC", title: "Live score tracking", desc: "Record match results and keep tournament progress clear during the event."},
    {code: "ST", title: "Standings and rankings", desc: "Track points, wins, losses and player rankings automatically."},
    {code: "CT", title: "Court scheduling", desc: "Organize matches across courts, rounds and time slots."},
    {code: "PL", title: "Player management", desc: "Manage players, teams, divisions and event communication."},
    {code: "PB", title: "Public results", desc: "Share fixtures, scores, standings and winners with players and spectators."},
];

const BENEFITS = [
    {title: "Less manual tournament admin", desc: "Reduce the time spent creating fixtures, collecting scores and updating tables."},
    {title: "Better player experience", desc: "Give players clearer fixtures, results and rankings throughout the event."},
    {title: "More flexible event formats", desc: "Run social events, competitive brackets, leagues and recurring club competitions."},
];

const USE_CASES = [
    {tag: "For Padel Clubs", title: "Run weekly tournaments and club nights", desc: "Organize Americano, Mexicano, King of the Court and ladder events for members."},
    {tag: "For Tournament Organizers", title: "Manage structured competitions", desc: "Create groups, brackets, standings and finals for competitive padel events."},
    {tag: "For Corporate Events", title: "Make social tournaments easy", desc: "Run fun padel events where players rotate partners, courts and opponents."},
    {tag: "For Padel Academies", title: "Track player progress", desc: "Use rankings, results and recurring competitions to motivate players."},
    {tag: "For Leagues", title: "Manage recurring fixtures", desc: "Run divisions, league tables, scheduled matches and ongoing standings."},
    {tag: "For Social Groups", title: "Keep events fun and organized", desc: "Generate fixtures quickly and keep everyone clear on their next match."},
];

const COMPARE = [
    {need: "Generate padel fixtures", manual: "Manual formulas and editing", scorepal: "Structured tournament formats"},
    {need: "Collect scores", manual: "WhatsApp, paper or manual entry", scorepal: "Centralized score tracking"},
    {need: "Update standings", manual: "Manual updates after each match", scorepal: "Clear live standings and rankings"},
    {need: "Manage courts", manual: "Easy to lose track during busy events", scorepal: "Organized by rounds, matches and courts"},
    {need: "Player experience", manual: "Players ask organizers for updates", scorepal: "Fixtures and results are easier to follow"},
];

const FAQS = [
    {
        q: 'What is padel tournament software?',
        a: <>Padel tournament software helps clubs and organizers create fixtures, manage players, record scores, update standings and publish results from one platform.</>,
    },
    {
        q: 'Can players register and sign up online?',
        a: <>Yes. Every padel event gets a public registration page where players sign up online — selecting their division, choosing a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe.</>,
    },
    {
        q: 'Can ScorePal run padel Americano tournaments?',
        a: <>Yes. ScorePal supports <Link to={"/americano-generator/" as any} className="text-[#082F8A] underline font-semibold">Americano</Link>-style padel events with rotating partners, fixtures, scores and individual standings.</>,
    },
    {
        q: 'Can ScorePal run Mexicano tournaments?',
        a: <>Yes. ScorePal supports <Link to={"/mexicano-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">Mexicano</Link>-style events where pairings may adapt based on results, rankings or player performance.</>,
    },
    {
        q: 'What is the best format for a padel club tournament?',
        a: <>For social club nights, Americano, Mexicano and King of the Court work well. For fair rankings, round robin or ladder leagues are often better. For competitive events, group and knockout or elimination brackets may be best — compare them on the <Link to={"/tournament-formats/" as any} className="text-[#082F8A] underline font-semibold">tournament formats</Link> page.</>,
    },
    {
        q: 'Can ScorePal manage padel live scores?',
        a: <>Yes. ScorePal helps organizers collect scores, update standings and keep tournament progress clear during an event.</>,
    },
    {
        q: 'Can ScorePal run a padel league instead of a one-day tournament?',
        a: <>Yes. For recurring fixtures, divisions, seasons and standings, see <Link to={"/padel-league-software/" as any} className="text-[#082F8A] underline font-semibold">padel league software</Link>. This page focuses on one-day and event-style tournaments.</>,
    },
    {
        q: 'Is ScorePal only for padel clubs?',
        a: <>No. ScorePal also supports tennis, pickleball, squash, badminton and other sports tournaments.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Padel Tournament Software for Clubs & Organizers | ScorePal</title>
            <meta name="description" content="Run padel tournaments with ScorePal. Create Americano, Mexicano, round robin, knockout, ladder league and King of the Court events with fixtures, live scores and standings." />
            <link rel="canonical" href={`${config.publicUrl}/padel-tournament-software/`} />
            <meta property="og:title" content="Padel Tournament Software | ScorePal" />
            <meta property="og:description" content="Create padel fixtures, manage players, track live scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/padel-tournament-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/padel-tournament-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Padel Tournament Software | ScorePal" />
            <meta name="twitter:description" content="Run padel tournaments, leagues and club events from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Padel Tournament Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Padel tournament software for creating fixtures, managing players, tracking live scores and publishing standings.",
                "url": "https://scorepal.co/padel-tournament-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is padel tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Padel tournament software helps clubs and organizers create fixtures, manage players, collect scores, publish standings and run padel events from one platform."}},
                    {"@type": "Question", "name": "Can players register and sign up online?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Every padel event gets a public registration page where players sign up online, selecting their division, choosing a partner for doubles, answering custom questions, accepting waivers and paying entry fees via Stripe."}},
                    {"@type": "Question", "name": "Can ScorePal run Americano and Mexicano padel tournaments?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal supports popular padel formats including Americano, Mexicano, round robin, King of the Court, ladder leagues and knockout tournaments."}},
                    {"@type": "Question", "name": "What is the best format for a padel club tournament?", "acceptedAnswer": {"@type": "Answer", "text": "For social club nights Americano, Mexicano and King of the Court work well; for fair rankings round robin or ladder leagues; for competitive events group and knockout or elimination brackets."}},
                    {"@type": "Question", "name": "Can ScorePal manage padel live scores and standings?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal helps organizers track scores, standings, rankings and tournament progress for padel events."}},
                    {"@type": "Question", "name": "Is ScorePal only for padel clubs?", "acceptedAnswer": {"@type": "Answer", "text": "No. ScorePal also supports tennis, pickleball, squash, badminton and other sports tournaments."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Padel Tournament Software", item: "https://scorepal.co/padel-tournament-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Padel Tournament Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Run padel tournaments without spreadsheets
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps padel clubs and organizers create fixtures, manage players, collect live
                                    scores, publish standings and run Americano, Mexicano, round robin, ladder and knockout
                                    events from one platform.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <a href="#formats" className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        View Padel Formats
                                    </a>
                                </div>
                            </div>

                            {/* Dashboard card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Everything your padel event needs</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Keep players, courts, scores and standings organized from the first fixture to the final result.</p>
                                <div className="space-y-3">
                                    {[
                                        {label: "Americano", value: "Fixtures ready"},
                                        {label: "Live Scores", value: "Updating"},
                                        {label: "Standings", value: "Auto-ranked"},
                                        {label: "Finals", value: "Next up"},
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3.5">
                                            <strong className="font-inter text-white text-sm">{row.label}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Formats */}
            <section id="formats" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Padel tournament formats ScorePal can support
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Whether you are running a social club night or a competitive championship, ScorePal helps you organize
                        the format, fixtures, scores and standings. Try a free generator for each:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FORMATS.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything padel organizers need in one place
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace manual scheduling, WhatsApp score collection and spreadsheet standings with software built for padel tournaments.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Club operations + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for real padel club operations
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            Padel events move quickly. Players need to know where they are playing, who they are playing with,
                            and what the standings look like. ScorePal keeps the whole event easier to manage.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Real product screenshot — live padel standings */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-3 lg:p-4 shadow-sm">
                        <img
                            src={shotStandings}
                            alt="ScorePal live padel league standings and rankings"
                            className="w-full h-auto rounded-2xl border border-[#e5e7eb]"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section id="use-cases" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for every type of padel event
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        ScorePal works for small social sessions, busy club nights and more structured competitive tournaments.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.tag} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <span className="inline-flex rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-3">{u.tag}</span>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for padel tournaments
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Spreadsheets work for planning, but they become hard to manage once players arrive, scores change and courts start moving.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA card */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Need more than a free padel generator?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            Use the free format generators to plan your event, then manage the full tournament in ScorePal with
                            players, courts, scores, standings and communication in one place.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">Get started - it's free</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                View Free Tools
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular padel tools</h3>
                        <ul className="space-y-2 font-inter text-sm text-white/85">
                            <li><Link to={"/americano-generator/" as any} className="hover:underline">Americano generator</Link></li>
                            <li><Link to={"/mexicano-tournament-generator/" as any} className="hover:underline">Mexicano generator</Link></li>
                            <li><Link to={"/king-of-the-court-generator/" as any} className="hover:underline">King of the Court generator</Link></li>
                            <li><Link to={"/ladder-league-generator/" as any} className="hover:underline">Ladder league generator</Link></li>
                            <li><Link to={"/round-robin-tournament-generator/" as any} className="hover:underline">Round robin generator</Link></li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Mobile scoring app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="inline-flex rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-3">Free iOS & Android app</span>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Score padel matches courtside from your phone
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            The free ScorePal app lets you record padel scores point-by-point on court, update live
                            standings instantly, and share a public live link so players and spectators follow every
                            result in real time — no laptop, no spreadsheet, no waiting until the end of the night.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-block">
                                <div className="bg-black rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={playStoreIcon} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-block">
                                <div className="bg-black rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={appStoreIcon} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <img
                            src={shotPhone}
                            alt="ScorePal padel scoring app running on a phone"
                            className="w-full max-w-sm h-auto object-contain"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* Padel proof — testimonial, real clubs, live event */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built with real padel clubs and leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        ScorePal runs live padel events every week — from club Americanos to full league seasons.
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 items-stretch">
                        {/* Testimonial */}
                        <figure className="bg-white border border-[#e5e7eb] rounded-3xl p-8 shadow-sm flex flex-col">
                            <span className="font-serif text-6xl leading-none text-[#082F8A]/20">&ldquo;</span>
                            <blockquote className="font-inter text-[#1a1a2e] text-lg italic leading-relaxed -mt-4">
                                {PAUL_QUOTE}
                            </blockquote>
                            <figcaption className="mt-6 flex items-center gap-3">
                                <img src={Paul_Gale} alt="Paul G." className="h-12 w-12 rounded-full object-cover ring-2 ring-[#082F8A]/15" />
                                <div>
                                    <div className="font-semibold text-[#1a1a2e]">Paul G.</div>
                                    <div className="text-sm text-[#1a1a2e]/60">Padel Chairman</div>
                                </div>
                            </figcaption>
                        </figure>

                        {/* Clubs + live event link */}
                        <div className="bg-white border border-[#e5e7eb] rounded-3xl p-8 shadow-sm flex flex-col justify-between">
                            <div>
                                <h3 className="font-inter font-semibold text-[#1a1a2e] mb-5">Padel clubs running on ScorePal</h3>
                                <div className="grid grid-cols-2 gap-5 items-center">
                                    {PADEL_BRANDS.map((b) => (
                                        <img key={b.alt} src={b.src} alt={b.alt} title={b.alt} className="h-12 w-auto object-contain mx-auto" loading="lazy" />
                                    ))}
                                </div>
                            </div>
                            <a href={LIVE_PADEL_EVENT_URL} target="_blank" rel="noopener noreferrer"
                               className="mt-8 inline-flex items-center gap-2 font-inter font-semibold text-[#082F8A] hover:underline">
                                See a live padel event
                                <ArrowUpRightIcon className="text-[#082F8A]" size={20} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next padel tournament with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Create fixtures, manage players, collect scores and publish standings with padel tournament software built for modern clubs and organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Americano", "Mexicano", "Round Robin", "King of the Court", "Live Scores", "Standings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Padel Tournament Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
